import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  IOrganisation,
  IOrganisationAccess,
  IOrganisationAccessRequestPayload,
  IOrganisationAccessUpdateRequest,
  IOrganisationControl,
  IOrganisationEventsResponse,
  IOrganisationGetResponseOk,
  IOrganisationPayload,
  IOrganisationPutResponseOk,
  IOrganisationRemoveMemberPayload,
  IOrganisationState,
  IOrganisationTeamsResponse,
  TOrganisationGetAllPayload,
} from '../../types/@organisation';

const initialState: IOrganisationState = {
  all: [],
  current: null,
  access: null,
  teams: null,
  events: null,
  control: {
    name: '',
    description: '',
    twitterId: '',
    instagramId: '',
    imagePath: '',
  },
  pages: undefined,
  isLoadingList: false,
  isLoadingAccess: false,
  isLoadingTeams: false,
  isLoadingEvents: false,
  isSavingAccess: false,
};

const organisationSlice = createSlice({
  name: 'organisations',
  initialState,
  reducers: {
    organisationControl: (state: IOrganisationState, { payload }: PayloadAction<IOrganisationControl>) => {
      return {
        ...state,
        control: payload,
      };
    },

    organisationControlReset: (state: IOrganisationState): IOrganisationState => {
      return {
        ...state,
        control: {
          name: '',
          description: '',
          twitterId: '',
          instagramId: '',
          imagePath: '',
        },
      };
    },

    setCurrentOrganisation: (state: IOrganisationState, { payload }: PayloadAction<IOrganisation | null>) => {
      return {
        ...state,
        current: payload,
      };
    },

    getOrganisations: (state: IOrganisationState, { payload }: PayloadAction<TOrganisationGetAllPayload>) => {
      return {
        ...state,
        isLoadingList: true,
      };
    },

    'getOrganisations/ok': (
      state: IOrganisationState,
      { payload }: PayloadAction<IOrganisationGetResponseOk>
    ): IOrganisationState => {
      return {
        ...state,
        all: payload.data,
        pages: payload.pages,
        isLoadingList: false,
      };
    },

    'getOrganisations/err': (state: IOrganisationState): IOrganisationState => {
      return {
        ...state,
        isLoadingList: false,
      };
    },

    putOrganisations: (state: IOrganisationState, { payload }: PayloadAction<IOrganisationPayload>) => state,

    'putOrganisations/ok': (
      state: IOrganisationState,
      { payload }: PayloadAction<IOrganisationPutResponseOk>
    ): IOrganisationState => {
      return state;
    },

    'putOrganisations/err': (state: IOrganisationState) => state,

    getOrganisationAccess: (
      state: IOrganisationState,
      { payload }: PayloadAction<IOrganisationAccessRequestPayload>
    ): IOrganisationState => {
      return {
        ...state,
        isLoadingAccess: true,
      };
    },

    'getOrganisationAccess/ok': (
      state: IOrganisationState,
      { payload }: PayloadAction<IOrganisationAccess>
    ): IOrganisationState => {
      return {
        ...state,
        access: payload,
        isLoadingAccess: false,
      };
    },

    'getOrganisationAccess/err': (state: IOrganisationState): IOrganisationState => {
      return {
        ...state,
        isLoadingAccess: false,
      };
    },

    getOrganisationTeams: (
      state: IOrganisationState,
      { payload }: PayloadAction<IOrganisationAccessRequestPayload>
    ): IOrganisationState => {
      return {
        ...state,
        isLoadingTeams: true,
      };
    },

    'getOrganisationTeams/ok': (
      state: IOrganisationState,
      { payload }: PayloadAction<IOrganisationTeamsResponse>
    ): IOrganisationState => {
      return {
        ...state,
        teams: payload,
        isLoadingTeams: false,
      };
    },

    'getOrganisationTeams/err': (state: IOrganisationState): IOrganisationState => {
      return {
        ...state,
        isLoadingTeams: false,
      };
    },

    getOrganisationEvents: (
      state: IOrganisationState,
      { payload }: PayloadAction<IOrganisationAccessRequestPayload>
    ): IOrganisationState => {
      return {
        ...state,
        isLoadingEvents: true,
      };
    },

    'getOrganisationEvents/ok': (
      state: IOrganisationState,
      { payload }: PayloadAction<IOrganisationEventsResponse>
    ): IOrganisationState => {
      return {
        ...state,
        events: payload,
        isLoadingEvents: false,
      };
    },

    'getOrganisationEvents/err': (state: IOrganisationState): IOrganisationState => {
      return {
        ...state,
        isLoadingEvents: false,
      };
    },

    updateOrganisationAccess: (
      state: IOrganisationState,
      { payload }: PayloadAction<IOrganisationAccessUpdateRequest>
    ): IOrganisationState => {
      return {
        ...state,
        isSavingAccess: true,
      };
    },

    'updateOrganisationAccess/ok': (
      state: IOrganisationState,
      { payload }: PayloadAction<IOrganisationAccess>
    ): IOrganisationState => {
      return {
        ...state,
        access: payload,
        isSavingAccess: false,
      };
    },

    'updateOrganisationAccess/err': (state: IOrganisationState): IOrganisationState => {
      return {
        ...state,
        isSavingAccess: false,
      };
    },

    removeOrganisationMember: (
      state: IOrganisationState,
      { payload }: PayloadAction<IOrganisationRemoveMemberPayload>
    ): IOrganisationState => {
      return {
        ...state,
        isSavingAccess: true,
      };
    },

    'removeOrganisationMember/ok': (state: IOrganisationState): IOrganisationState => {
      return {
        ...state,
        isSavingAccess: false,
      };
    },

    'removeOrganisationMember/err': (state: IOrganisationState): IOrganisationState => {
      return {
        ...state,
        isSavingAccess: false,
      };
    },

    leaveOrganisation: (
      state: IOrganisationState,
      { payload }: PayloadAction<IOrganisationAccessRequestPayload>
    ): IOrganisationState => {
      return {
        ...state,
        isSavingAccess: true,
      };
    },

    'leaveOrganisation/ok': (state: IOrganisationState): IOrganisationState => {
      return {
        ...state,
        isSavingAccess: false,
      };
    },

    'leaveOrganisation/err': (state: IOrganisationState): IOrganisationState => {
      return {
        ...state,
        isSavingAccess: false,
      };
    },
  },
});

export const organisationActions = organisationSlice.actions;
export const organisationReducer = organisationSlice.reducer;